// @flow
import { Typography } from "@mui/material";
import './card.css'
import Box from "@mui/material/Box";
import * as React from 'react';
import { useMemo, useState } from 'react';
import type {SyntheticEvent} from 'react';
import { normalizeGreek } from "../../../../helpers/normalizeGreek";

type Props = {
	image?: string,
	preText?: string,
	title?: string,
	text?: string,
	action?: React.Node,
	video?: string,
	textColor?: string,
	backgroundColor?: string,
	index: number,
	length: number,
};

const getYouTubeVideoId = (videoUrl: string) => {
	try {
		const url = new URL(videoUrl)
		const pathParts = url.pathname.split('/').filter(Boolean)
		return url.hostname === 'youtu.be'
			? pathParts[0]
			: pathParts[0] === 'embed' || pathParts[0] === 'shorts'
				? pathParts[1]
				: url.searchParams.get('v')
	} catch {
		return null
	}
}

const getYouTubePlayerUrl = (videoUrl: string, autoplay: boolean) => {
	try {
		const url = new URL(videoUrl)
		url.searchParams.set('rel', '0')
		if (autoplay) url.searchParams.set('autoplay', '1')
		return url.toString()
	} catch {
		return videoUrl
	}
}

const Card = (props: Props) => {
	const {title, preText, text, action, image, textColor, video, index, length, backgroundColor} = props
	const [isVideoPlaying, setIsVideoPlaying] = useState(false)
	const [thumbnailQuality, setThumbnailQuality] = useState('maxresdefault')
	const bannerWidth = useMemo(() => {
		switch (length) {
			case 1:
				return '100%'
			case 2:
				return '44%'
			default:
				return '30%'
			
		}
	}, [length])
	const videoId = video ? getYouTubeVideoId(video) : null
	const thumbnailUrl = videoId
		? `https://i.ytimg.com/vi/${videoId}/${thumbnailQuality}.jpg`
		: null
	const handleThumbnailError = () => {
		if (thumbnailQuality === 'maxresdefault') {
			setThumbnailQuality('hqdefault')
		} else {
			setIsVideoPlaying(true)
		}
	}
	const handleThumbnailLoad = (event: SyntheticEvent<HTMLImageElement>) => {
		if (thumbnailQuality === 'maxresdefault' && event.currentTarget.naturalWidth <= 120) {
			setThumbnailQuality('hqdefault')
		}
	}

	return (
		<Box
			className={`cardKpi${video ? ' cardKpi--video' : ''}`}
			sx={{
				...(image ? { backgroundImage: `url("${image}")` } : {}),
				...(!image ? {backgroundColor: backgroundColor ? `#${backgroundColor}` : '#485776'} : {}),
				color: textColor ? `#${textColor}` : null,
				width: {
					sm: '100%',
					md: bannerWidth
				},
				backgroundRepeat: 'no-repeat',
				backgroundSize: 'contain',
				backgroundPosition: 'center center'
			}}
		>
			<Box className={'cardKpi__content'}>
				{preText && (
					<Typography
						component={'div'}
						variant={'overline'}
						className={'cardKpi__pre-text'}
						pb={0.3}
					>
						{normalizeGreek(preText)}
					</Typography>
				)}
				<Typography
					component={'div'}
					variant={'h3'}
					pb={1}
				>
					{title}
				</Typography>
				<Typography
					className={'cardKpi__description'}
					component={'div'}
					variant={'body1'}
				>
					{text}
				</Typography>
				{video && (
					<Box className="cardKpi__video">
						{!isVideoPlaying && thumbnailUrl ? (
							<button
								type="button"
								className="cardKpi__video-preview"
								onClick={() => setIsVideoPlaying(true)}
								aria-label="Play video"
							>
								<img
									src={thumbnailUrl}
									alt=""
									onError={handleThumbnailError}
									onLoad={handleThumbnailLoad}
								/>
								<span className="cardKpi__play-icon" aria-hidden="true" />
							</button>
						) : (
							<iframe
								src={getYouTubePlayerUrl(video, isVideoPlaying)}
								title="YouTube video player"
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
								referrerPolicy="strict-origin-when-cross-origin"
								allowFullScreen
							/>
						)}
					</Box>
				)}
				{action && (
					<Box className={'cardKpi__action'}>
						{action}
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default Card